import { ArrowRight } from "lucide-react";
import { Link } from "react-router";
import { useProjectAskedLinks } from "@/features/brain/links-api";
import { useDocuments, useImports } from "@/features/project/documents-api";
import { cn } from "@/lib/utils";
import { documentsWord, projectReviewFacts } from "./review-facts";
import { projectReviewPath } from "./review-api";

/**
 * One line for everything that waits for a person, and the way in
 * (2026-10-03, the owner): "Document(s) ready for review: 15 documents
 * awaiting." with "Review Documents".
 *
 * **One line for the project, not one per import.** It was one per import
 * (Phase 3f, 2026-09-22), with a second notice for the check's open questions
 * (HID-04, 2026-09-26). A project uploaded one file at a time showed one
 * line per file, in two colours, and the same file could be in both. The
 * count is of files, each once, whatever it waits for: a decision among its
 * versions, hide or keep, a link from it, or a hide in an import nobody has
 * looked at yet. The button opens one review of the whole project.
 *
 * **It stays until each question has an answer** (HID-04): an import marked
 * reviewed with a question open still counts that file. Links are counted once
 * their list has loaded; until then the count leaves them out, rather than
 * waiting.
 *
 * Renders nothing when nothing waits. The review is not a standing to-do,
 * and an empty banner would make it one.
 */
export function ImportReviewBanner({
  projectId,
  className,
}: {
  projectId: string;
  className?: string;
}) {
  const { data: imports } = useImports(projectId);
  const { data: documents } = useDocuments(projectId);
  const { data: askedLinks } = useProjectAskedLinks(projectId);
  // Both, or nothing: a count drawn before the documents arrive would be
  // wrong for as long as the request takes.
  if (!imports || !documents) return null;

  const { awaiting } = projectReviewFacts(imports, documents, askedLinks ?? []);
  if (awaiting === 0) return null;

  return (
    <section
      aria-label="Documents ready for review"
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 rounded-card-lg border-[1.5px] border-brand/30 bg-brand-tint px-4 py-3",
        className,
      )}
    >
      <p className="text-[0.88rem] text-ink">
        <span className="font-semibold">Document(s) ready for review:</span> {documentsWord(awaiting)} awaiting.
      </p>
      <Link
        to={projectReviewPath(projectId)}
        className="inline-flex items-center gap-1.5 text-[0.85rem] font-bold text-brand hover:text-brand-hover"
      >
        Review Documents
        <ArrowRight size={15} aria-hidden />
      </Link>
    </section>
  );
}
